import { capRefusal, merchantCapSource } from "../promotions/cap";
import type { DiscountBlock } from "./types";
import type { BillDiscount, DiscountOffer, Sale } from "../promotions/types";

/**
 * Is this discount offerable, and may it be applied to THIS bill?
 *
 * Pure — every input is an argument, so each rule is testable with plain objects and neither
 * platform's store is involved. That is what let the applicability rule below stop being
 * web-only; see its header.
 *
 * The rules are ordered as the flow asks them: does the cart contain what the offer needs, is the
 * merchant's count limit reached, and does it need a note first.
 */

/* ── does the cart contain what the discount needs ─────────────────────────────────────── */

/**
 * Does the cart contain at least one item this discount applies to?
 *
 * A discount that names no items and no categories applies to the whole bill and answers `true`
 * with no work. Otherwise an item qualifies when it is named directly, or its category is named
 * — and, where that category restricts sub-categories, its sub-category is one of them.
 *
 * 🔴 **This is `validateDiscountApplicability` with its sense flipped and its store read
 * removed.** The original returned "NOT applicable" — callers read it as `if (validate…) block`
 * — which is a predicate whose `true` means failure, and it read the sale off the store. Both
 * were why it lived in `helpers/discountHelper` as a WEB-ONLY module: its `arrayToMap` call
 * returns `null` on web and `{}` on mobile, so the helper could not be shared and the whole
 * flow was pinned to desktop behind it. The category map is built here with a plain reduce, the
 * sale arrives as an argument, and the rule is the same rule.
 *
 * ⚠️ **One definition.** `helpers/discountHelper` no longer has a copy — its
 * `validateDiscountApplicability` is now a four-line adapter over this function that flips the
 * sense back and reads the sale off the store, so web's existing importers are untouched and
 * there is nothing to drift.
 */
export const cartHasDiscountItems = (
  offer: DiscountOffer | null | undefined,
  sale: Sale | null | undefined,
): boolean => {
  const itemIds = new Set(offer?.itemIds ?? []);
  const categoryIds = new Set(offer?.categoryIds ?? []);
  if (itemIds.size === 0 && categoryIds.size === 0) return true;

  const subCategoriesByCategory = (offer?.categorySubCategoryIds ?? []).reduce<
    Record<string, string[] | undefined>
  >((map, entry: any) => {
    if (entry?.id) map[entry.id] = entry.subCategoryIds;
    return map;
  }, {});

  return ((sale as any)?.saleItems ?? []).some((saleItem: any) => {
    if (itemIds.has(saleItem?.itemId)) return true;
    if (!categoryIds.has(saleItem?.categoryId)) return false;
    const allowedSubCategories = subCategoriesByCategory[saleItem?.categoryId];
    // A named category with no sub-category restriction covers every item in it.
    if (!allowedSubCategories?.length) return true;
    return allowedSubCategories.includes(saleItem?.subCategoryId);
  });
};

/* ── the composed gate ─────────────────────────────────────────────────────────────────── */

/**
 * Every reason this discount may not be applied right now, or `null`.
 *
 * 🔴 **One gate, asked once, in a fixed order** — and the order is the behaviour, not a
 * preference. The old component asked these as four separate early-returns in `applyDiscount`
 * whose sequence differed from `applyCoupon`'s, so a coupon over the count limit was refused
 * with "not applicable" on one path and with the limit message on the other.
 *
 * `notes-required` is LAST on purpose: it is the only block a cashier can clear from the dialog
 * the caller then opens, so asking for a note before checking whether the discount could apply
 * at all makes the cashier type a reason for a refusal.
 *
 * @param existingExternal - the partner offer already on the bill.
 *   Only blocks when the incoming discount is itself external.
 */
export const findDiscountBlock = (
  offer: DiscountOffer,
  {
    sale,
    businessData,
    existingExternal,
    skipNotes = false,
  }: {
    sale: Sale;
    businessData: Record<string, any>;
    existingExternal?: BillDiscount | null;
    /** Customer-facing surfaces have nobody to type a note — see `useKioskOffers`. */
    skipNotes?: boolean;
  },
): DiscountBlock | null => {
  if (capRefusal(offer, sale, [merchantCapSource(businessData)]) !== null) return { reason: "limit-reached" };
  if (!cartHasDiscountItems(offer, sale)) return { reason: "not-applicable" };
  if (offer?.isExternal && existingExternal) {
    return { reason: "external-already-applied", existing: existingExternal };
  }
  /**
   * ⚠️ **A PARTNER offer never asks for a note**, even one whose record sets `notesRequired`.
   * Web's `applyDiscount` tested `isExternal` BEFORE `notesRequired` and returned, so the partner
   * path skipped the prompt on the way out — and skipped it again on the way back in, because the
   * resolved apply passed `checkForReason: false`. Preserved as behaviour rather than as an
   * accident of statement order: routing a partner offer through the reason prompt would apply it
   * to the BILL from the prompt's confirm instead of sending it to the partner, which is the bug
   * this ordering prevents.
   */
  if (offer?.isExternal) return null;
  if (!skipNotes && offer?.notesRequired) return { reason: "notes-required" };
  return null;
};
